import { inject, Injectable } from '@angular/core';
import {
  BasicDialogService,
  DialogPresets,
} from '@principle-theorem/ng-shared';
import { Patient } from '@principle-theorem/principle-core';
import {
  type IPatient,
  isPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  type WithRef,
  deletedQuery,
  isWithRef,
  query$,
  snapshot,
} from '@principle-theorem/shared';
import { type Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ManagementResource } from '../models/management-resource';
import {
  type IManagementTool,
  type IToolRunContext,
} from '../models/management-tool';
import {
  type IRestoreDeletedMediaDialogData,
  RestoreDeletedMediaDialogComponent,
} from '../restore-deleted-media-dialog/restore-deleted-media-dialog.component';

function isPatientWithRef(value: unknown): value is WithRef<IPatient> {
  return isPatient(value) && isWithRef<IPatient>(value);
}

@Injectable({ providedIn: 'root' })
export class RestoreDeletedMediaTool implements IManagementTool {
  private _dialog = inject(BasicDialogService);

  readonly name = 'restore-deleted-media';
  readonly label = 'Restore Deleted Media';
  readonly icon = 'restore_from_trash';
  readonly description =
    'Select and restore soft-deleted media files for this patient';
  readonly resource = ManagementResource.Patient;

  isDisabled(context: IToolRunContext): Observable<boolean> {
    if (!context.resource) {
      return of(true);
    }
    return context.resource.value$.pipe(
      switchMap((value) => {
        if (!value || !isPatient(value) || !isWithRef<IPatient>(value)) {
          return of(true);
        }
        return query$(deletedQuery(Patient.mediaCol(value))).pipe(
          map((deletedMedia) => deletedMedia.length === 0)
        );
      })
    );
  }

  async run(context: IToolRunContext): Promise<void> {
    if (!context.resource) {
      return;
    }
    const value = await snapshot(context.resource.value$);
    if (!value || !isPatientWithRef(value)) {
      return;
    }
    const data: IRestoreDeletedMediaDialogData = { patient: value };
    this._dialog.open(
      RestoreDeletedMediaDialogComponent,
      DialogPresets.medium({ data })
    );
  }
}
